import {
  EditFilled,
  LockFilled,
  QuestionCircleOutlined,
  StopOutlined,
  SwapOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import {
  Button,
  Descriptions,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Switch,
  Tag
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { SettingCard } from "~/components/dataCards/SettingCard.jsx";
import { $t } from "~/i18n.js";
import { selectElementByID } from "../../lib/helpers.js";
import { IGCButton } from "../IGC/IGC.button.jsx";
import { AnnuleerButton } from "../buttons/AnnuleerButton.jsx";
import { SaveButton } from "../buttons/SaveButton.jsx";
import { FlightTacometer } from "../form/FlightTacometer.jsx";
import { LandingIcon, StartIcon } from "../icon/icon.jsx";
import { SelectPilot } from "../profile/selectPilot.jsx";
import { FlightOpenIcon } from "./FlightOpenIcon.jsx";
import { HeightValue } from "./sections/HeighValue.jsx";
import { PayingMemberSelector } from "./sections/PayingMemberSelector.jsx";
import { FlightDetails } from "./sections/flightDetails.jsx";
import { SectionChecks } from "./sections/sectionChecks.jsx";
import { SectionKist } from "./sections/sectionKist.jsx";
import { SectionSleepStartMethode } from "./sections/sectionSleepStartMethode.jsx";
import { SectionStartmethode } from "./sections/sectionStartmethode.jsx";
import { SectionTijd } from "./sections/sectionTijd.jsx";
import { SignFlightCollapse } from "./sections/signFlight.jsx";
import { SleepActions } from "./sections/sleepActions.jsx";

export const FlightEditModal = ({
  visible,
  onClose,
  readOnly,
  currentUser,
  flight,
  sleepFlight,
  updateFlight,
  kisten,
  sleep_kisten,
  vliegers,
  aanmeldingen,
  setSelectedPilot,
  openConnectedFlight = null,
  disconnectSleepFlight,
  connectSleepFlight,
  addNewSleepFlight,
  allFlights,
  club
}) => {
  const [visibleFlight, setVisibleFlight] = useState({});
  const [visibleSleepFlight, setVisibleSleepFlight] = useState({});

  const editInstructorDetails = currentUser.is_instructeur;
  const wijzigVluchtAlsAdmin = !!currentUser?.wijzig_vluchten;

  // Full-data editing is reserved for admin or a pilot involved in this flight/sleep flight.
  const canEditAllFlightData =
    wijzigVluchtAlsAdmin ||
    currentUser.id === flight.gezagvoerder_id ||
    currentUser.id === flight.tweede_inzittende_id ||
    currentUser.id === visibleSleepFlight?.gezagvoerder_id ||
    currentUser.id === visibleSleepFlight?.tweede_inzittende_id ||
    
    // or if no pilot has been filled out yet
    !flight.gezagvoerder_id;
  
  const [forceEdit, setForceEdit] = useState(false);

  // For pilot data, all members can edit if not locked
  const readOnlyLimitedData =
    !forceEdit &&
    (readOnly ||
      (visibleFlight && (visibleFlight.is_deleted || visibleFlight.is_locked)));
  
  // Remainder data is readonly when limited data is readonly OR user is not part of the flight.
  const readOnlyAllData = readOnlyLimitedData || !canEditAllFlightData;

  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (flight) {
      flight && setVisibleFlight(flight);
      sleepFlight && setVisibleSleepFlight(sleepFlight);
      setChanges([]);
      setLoading(false);
    }
  }, [flight, sleepFlight]);

  // check if selectedKist already exists, required for kist and tweede_inzittende registration
  const selectedKist = selectElementByID(
    kisten,
    "registratie",
    visibleFlight && visibleFlight.registratie,
  );
  const selectedSleepKist = selectElementByID(
    sleep_kisten,
    "registratie",
    visibleSleepFlight && visibleSleepFlight.registratie,
  );

  const gezagVoerder = selectElementByID(
    vliegers,
    "id",
    visibleFlight && visibleFlight.gezagvoerder_id,
  );
  const tweedeInzittende = selectElementByID(
    vliegers,
    "id",
    visibleFlight && visibleFlight.tweede_inzittende_id,
  );
  const sleepVlieger = selectElementByID(
    vliegers,
    "id",
    visibleSleepFlight && visibleSleepFlight.gezagvoerder_id,
  );

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errors, setErrors] = useState([]);

  const validateFlight = () => {
    const errors = [];
    if (!visibleFlight.gezagvoerder_naam) {
      errors.push($t("Voeg een gezagvoerder toe."));
    }
    if (!visibleFlight.registratie) errors.push($t("Voeg een kist."));
    if (!visibleFlight.start_tijd) errors.push($t("Voeg start tijd toe."));
    if (!visibleFlight.landings_tijd) errors.push($t("Voeg landingstijd toe."));
    if (visibleFlight?.vluchtduur < 0) {
      errors.push($t("Je kan geen negatieve vluchtduur hebben."));
    }

    // double check on save flight if the betalend lid id/voucher_id is the same
    const betalend_lid_id = visibleFlight.betalend_lid_id;
    const voucher_id = visibleFlight.voucher_id;
    if (
      visibleSleepFlight &&
      (visibleSleepFlight.betalend_lid_id !== betalend_lid_id ||
        visibleSleepFlight.voucher_id !== voucher_id)
    ) {
      saveSleepFlight({
        betalend_lid_id: betalend_lid_id,
        voucher_id: voucher_id,
      });
    }

    setErrors(errors);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => setShowConfirmModal(false);
  const saveFlight = (newData, change) => {
    setVisibleFlight({ ...visibleFlight, ...newData });
    if (change && changes.indexOf(change) === -1) {
      setChanges([change].concat(changes));
    }
  };
  const saveSleepFlight = (newData, change) => {
    // note for the modal, we always save the complete flight element
    if (sleepFlight) {
      setVisibleSleepFlight({ ...visibleSleepFlight, ...newData });
      if (change && changes.indexOf(change) === -1) {
        setChanges([change].concat(changes));
      }
    }
  };

  const saveAndClose = async () => {
    setLoading(true);
    if (sleepFlight) {
      await updateFlight(visibleSleepFlight);
    }
    const data = await updateFlight(visibleFlight);
    setLoading(false);
    if (data) {
      setShowConfirmModal(false);
      onClose();
    }
  };

  const showDeleteModal = () => {
    Modal.confirm({
      title: $t("Weet je zeker dat je de vlucht wilt verwijderen?"),
      icon: <QuestionCircleOutlined />,
      okText: $t("Ja"),
      okType: "danger",
      cancelText: $t("Nee"),
      onOk() {
        return triggerDelete();
      },
    });
  };

  const triggerDelete = async () => {
    setLoading(true);
    const data = await updateFlight({
      uuid: visibleFlight.uuid,
      action: "delete",
    });
    sleepFlight &&
      (await updateFlight({ uuid: sleepFlight.uuid, action: "delete" }));
    setLoading(false);
    if (data) onClose();
  };

  const [saverRecover, setSaveRecover] = useState(false);
  const recoverFlight = async () => {
    setSaveRecover(true);
    const data = await updateFlight({
      uuid: visibleFlight.uuid,
      action: "recover",
    });
    sleepFlight &&
      (await updateFlight({ uuid: sleepFlight.uuid, action: "recover" }));
    setSaveRecover(false);
    if (data) onClose();
  };


  return (
    <Modal
      width={600}
      title={
        visibleFlight.volg_nummer ? (
          <span>
            {$t("Vlucht")} #{visibleFlight.volg_nummer}
          </span>
        ) : (
          $t("Voeg vlucht toe")
        )
      }
      open={visible}
      onCancel={onClose}
      footer={[
        flight.is_deleted && (
          <Button
            loading={saverRecover}
            disabled={
              !wijzigVluchtAlsAdmin || (visibleFlight.is_locked && !forceEdit)
            }
            onClick={recoverFlight}
          >
            {$t("Herstel")}
          </Button>
        ),
        !flight.is_deleted && (
          <Popconfirm
            placement="bottom"
            title={$t("Zeker weten dat je de vlucht wilt verwijderen")}
            onConfirm={showDeleteModal}
            key="delete"
            disabled={readOnlyAllData}
            okText={$t("Ja")}
            cancelText={$t("Nee")}
          >
            <Button
              icon={<StopOutlined />}
              danger
              loading={loading}
              disabled={readOnlyAllData}
            >
              {$t("Verwijder")}
            </Button>
          </Popconfirm>
        ),

        <AnnuleerButton
          key="annuleren"
          style={{ marginLeft: 8 }}
          onClick={onClose}
        />,

        <SaveButton
          style={{ marginLeft: 8 }}
          key="opslaan"
          onClick={() => validateFlight()}
          disabled={readOnlyAllData && changes.length === 0}
        />,
      ]}
    >
      <Modal
        title={$t("Bevestig wijzigingen")}
        open={showConfirmModal}
        onCancel={closeConfirmModal}
        footer={
          errors.length > 0
            ? [
                <Button key="terug" onClick={closeConfirmModal}>
                  {$t("Terug")}
                </Button>,
              ]
            : [
                <AnnuleerButton key="annuleren" onClick={closeConfirmModal} />,
                <Button
                  key="opslaan"
                  onClick={saveAndClose}
                  type="primary"
                  loading={loading}
                >
                  {$t("Bevestig")}
                </Button>,
              ]
        }
      >
        {errors.length > 0 ? (
          <div style={{ marginLeft: 12 }}>
            <p>{$t("Corrigeer de volgende fouten")}:</p>
            <ul>
              {errors.map((value, key) => (
                <li key={key}>{value}</li>
              ))}
            </ul>
          </div>
        ) : (
          <div style={{ marginLeft: 12 }}>
            {visibleFlight.id ? (
              <>
                <p className="spacer">
                  {$t("Klopt het dat je het volgende wilt wijzigen")}?
                </p>
                <ul>
                  {changes.map((change, key) => (
                    <li key={key}>{change}</li>
                  ))}
                </ul>
                <p className="small" style={{ margin: "12px 0" }}>
                  {$t(
                    "Alle correcties zullen worden bijgehouden en doorgestuurd naar de admin, DDI en piloot in kwestie ter controle",
                  )}
                  .
                </p>
              </>
            ) : (
              <p>{$t("Klopt het dat je een vlucht wilt toevoegen")}?</p>
            )}
          </div>
        )}
      </Modal>

      <Flex gap={12} vertical>
        {wijzigVluchtAlsAdmin && flight.is_deleted && (
          <div className="row small" style={{ gap: 6 }}>
            <Tag color="red">{$t("Verwijderd")}</Tag>{" "}
            <p>
              {$t("Als vlucht admin zie je ook alle verwijderde vluchten.")}
            </p>
          </div>
        )}

        {wijzigVluchtAlsAdmin && visibleFlight.is_locked && (
          <div className="row small">
            <span>{$t("Wijzig vlucht als admin")}:</span>
            <Switch
              size="small"
              style={{ marginLeft: 6 }}
              checked={forceEdit}
              onChange={setForceEdit}
            />
          </div>
        )}

        <SettingCard>
          <b className="label">{$t("Kist")}</b>

          <SectionStartmethode
            extra_methods={
              selectedKist?.category == "sleep" ? ["tmg-a", "sep-a"] : []
            }
            flight={flight}
            // because the start method updates the selected flight, we always save the flight directly
            saveFlight={async (data) =>
              await updateFlight({
                uuid: flight.uuid,
                ...visibleFlight,
                ...data,
              })
            }
            readOnly={
              readOnlyAllData ||
              (visibleFlight &&
                visibleFlight.start_methode === "sleep" &&
                visibleFlight.sleep_uuid)
            }
            size="small"
          />

          <SectionKist
            selectedKist={selectedKist}
            kisten={kisten}
            flight={visibleFlight}
            saveFlight={async (d) => await saveFlight(d, "Kist")}
            readOnly={readOnlyAllData}
            vliegers={vliegers}
            selectDefaultPilot={true}
          />

          {(flight.sleep_uuid ||
            ["sleep", "tmg-a", "sep-a"].includes(flight.start_methode)) && (
            <section style={{ marginTop: 18 }}>
              <b className="label">{$t("Sleepkist")}</b>

              <div className="column" style={{ alignItems: "flex-start" }}>
                {sleepFlight && (
                  <>
                    <SectionSleepStartMethode
                      flight={visibleSleepFlight}
                      saveFlight={saveSleepFlight}
                      readOnly={readOnlyAllData}
                      loading={loading}
                    />

                    <SectionKist
                      isSleep={true}
                      selectedKist={selectedSleepKist}
                      kisten={sleep_kisten}
                      flight={visibleSleepFlight}
                      saveFlight={saveSleepFlight}
                      readOnly={readOnlyAllData}
                    />
                  </>
                )}

                {addNewSleepFlight && (
                  <div style={{ marginTop: 12 }}>
                    <SleepActions
                      flight={flight}
                      saveFlight={saveFlight}
                      allFlights={allFlights}
                      sleepFlight={sleepFlight}
                      readOnly={readOnlyAllData}
                      disconnectSleepFlight={disconnectSleepFlight}
                      connectSleepFlight={connectSleepFlight}
                      addNewSleepFlight={addNewSleepFlight}
                      sleepKisten={sleep_kisten}
                    />
                  </div>
                )}
              </div>

              {openConnectedFlight && flight.sleep_uuid && (
                <div style={{ marginTop: 12 }}>
                  <p>
                    {$t("Bekijk verbonden vlucht")}:{" "}
                    <FlightOpenIcon flight_uuid={flight.sleep_uuid} />
                  </p>
                </div>
              )}
            </section>
          )}
        </SettingCard>
        <SettingCard>
          <b className="label">{$t("Vliegers")}</b>

          <SelectPilot
            readOnly={readOnlyLimitedData || !visibleFlight.registratie}
            field_id="gezagvoerder_id"
            field_name="gezagvoerder_naam"
            title={
              selectedKist && selectedKist.pilots === 2
                ? $t("PIC/instructeur")
                : $t("PIC")
            }
            pilot={gezagVoerder}
            otherPilot={tweedeInzittende}
            vliegers={aanmeldingen}
            alle_vliegers={vliegers}
            flight={visibleFlight}
            saveFlight={(d) => saveFlight(d, $t("Gezagvoerder"))}
            openPilot={
              setSelectedPilot ? () => setSelectedPilot(gezagVoerder) : null
            }
            kist_in_dto={selectedKist?.in_dto}
            addUnknown={club?.default_vliegers !== '--'}
          />

          {(!selectedKist ||
            selectedKist.pilots === 2 ||
            visibleFlight?.tweede_inzittende_naam) && (
            <Button
              disabled={
                readOnlyAllData ||
                !visibleFlight?.tweede_inzittende_id ||
                !visibleFlight?.gezagvoerder_id
              }
              shape="circle"
              icon={<SwapOutlined fill="red" />}
              size="small"
              type="text"
              style={{ marginLeft: 25 }}
              onClick={() =>
                saveFlight(
                  {
                    betalend_lid_id: flight.gezagvoerder_id,
                    gezagvoerder_id: visibleFlight.tweede_inzittende_id,
                    gezagvoerder_naam: visibleFlight.tweede_inzittende_naam,
                    tweede_inzittende_id: visibleFlight.gezagvoerder_id,
                    tweede_inzittende_naam: visibleFlight.gezagvoerder_naam,
                    is_fis:
                      selectedKist?.in_dto &&
                      tweedeInzittende &&
                      tweedeInzittende.is_instructeur
                        ? true
                        : false,
                    is_training: false,
                    is_examen: false,
                    is_profcheck: false,
                  },
                  $t("Vliegers gewisseld"),
                )
              }
            />
          )}

          {(!selectedKist ||
            selectedKist.pilots === 2 ||
            visibleFlight.tweede_inzittende_naam) && (
            <SelectPilot
              key={`selectpilot-tweedeinzittende-${visibleFlight?.id || ""}`}
              readOnly={readOnlyLimitedData || !visibleFlight.registratie}
              field_id="tweede_inzittende_id"
              field_name="tweede_inzittende_naam"
              title={$t("Pax/DBO")}
              pilot={tweedeInzittende}
              otherPilot={gezagVoerder}
              gezagVoerder={gezagVoerder}
              vliegers={aanmeldingen}
              alle_vliegers={vliegers}
              flight={visibleFlight}
              saveFlight={(d) => saveFlight(d, $t("Tweede inzittende"))}
              openPilot={
                setSelectedPilot
                  ? () => setSelectedPilot(tweedeInzittende)
                  : null
              }
              kist_in_dto={selectedKist?.in_dto}
              addUnknown={club?.default_vliegers !== '--'}
            />
          )}

          {sleepFlight && (
            <div className="row" style={{ marginTop: 10 }}>
              <p style={{ marginRight: 6 }} className="capitalize">
                {$t("sleepvlieger")}:
              </p>
              <SelectPilot
                key={`selectpilot-sleepvlieger-${visibleSleepFlight?.id || ""}`}
                hideAvatar
                readOnly={readOnlyLimitedData}
                field_id="gezagvoerder_id"
                field_name="gezagvoerder_naam"
                title={$t("sleepvlieger")}
                pilot={sleepVlieger}
                otherPilot={null}
                // vliegers gezagvoerder only non-pax
                vliegers={aanmeldingen.filter(
                  (aanmelding) =>
                    aanmelding.id > 0 && aanmelding.as_sleepvlieger,
                )}
                alle_vliegers={vliegers}
                flight={visibleSleepFlight}
                saveFlight={(d) => saveSleepFlight(d, $t("sleepvlieger"))}
                openPilot={
                  setSelectedPilot ? () => setSelectedPilot(sleepVlieger) : null
                }
                kist_in_dto={false}
                addUnknown={club?.default_vliegers !== '--'}
              />
            </div>
          )}

          <div
            className="row"
            style={{ marginTop: 18, alignItems: "flex-start" }}
          >
            <p style={{ marginRight: 6 }}>{$t("Betalend lid")}:</p>
            <div style={{ maxWidth: 270 }}>
              <PayingMemberSelector
                value={visibleFlight.betalend_lid_id}
                onChange={(v) => {
                  saveFlight({ betalend_lid_id: v });
                  saveSleepFlight({ betalend_lid_id: v });
                }}
                vliegers={vliegers}
                flight={visibleFlight}
                disabled={readOnlyLimitedData || !visibleFlight.registratie}
              />
            </div>
          </div>
        </SettingCard>
        <SettingCard>
          <b className="label">{$t("Tijden")}</b>

          <SectionTijd
            disableNumberOfStarts
            readOnly={readOnlyAllData || !visibleFlight.registratie}
            updateFlightForMotor={updateFlight}
            flight={visibleFlight}
            saveFlight={(d) => {
              // To connect with sleep, save start tijd
              if ("start_tijd" in d) {
                let sleepVluchtduur = 0;
                if (visibleSleepFlight.landings_tijd) {
                  sleepVluchtduur = parseInt(
                    (dayjs(visibleSleepFlight.landings_tijd, "HH:mm") -
                      dayjs(d.start_tijd, "HH:mm")) /
                      60000,
                  );
                }
                saveSleepFlight({
                  start_tijd: d.start_tijd,
                  landings_tijd: visibleSleepFlight.landings_tijd,
                  vluchtduur: sleepVluchtduur,
                });
              }

              saveFlight(d, $t("Vlucht tijden"));
            }}
          />

          {selectedKist && selectedKist.motor_byflight && (
            <div style={{ marginTop: 6 }}>
              <FlightTacometer
                motor_format={selectedKist.motor_format}
                flight={visibleFlight}
                readOnly={readOnlyAllData || (flight.is_locked && !forceEdit)}
                saveFlight={async (d) => {
                  // if flight is not landed yet, we save directly
                  if (!visibleFlight.landings_tijd) {
                    const data = await updateFlight({
                      uuid: visibleFlight.uuid,
                      ...d,
                    });
                    if (data) {
                      setVisibleFlight({ ...visibleFlight, ...d });
                    }
                  }

                  // otherwise save via modal
                  return saveFlight(d, $t("Vlucht tijden"));
                }}
              />
            </div>
          )}

          {sleepFlight && (
            <section style={{ marginTop: 12 }}>
              <b className="label">{$t("Sleep tijden")}</b>

              <SectionTijd
                disableNumberOfStarts
                readOnly={readOnlyAllData || !visibleSleepFlight.registratie}
                flight={visibleSleepFlight}
                saveFlight={(d) => {
                  // update possible flightDuur, don't connect to sleep
                  saveSleepFlight(d, $t("Vlucht tijden"));
                }}
              />
            </section>
          )}
        </SettingCard>
        <SettingCard>
          <b className="label">{$t("Details")}</b>

          <div style={{ width: 200 }}>
            <SectionChecks
              readOnly={readOnlyAllData}
              editInstructorDetails={editInstructorDetails}
              flight={visibleFlight}
              saveFlight={(d) => saveFlight(d, $t("Details"))}
              sleepFlight={visibleSleepFlight}
              saveSleepFlight={(d) => saveSleepFlight(d, $t("Details"))}
              gezagvoerder={gezagVoerder}
              tweedeInzittende={tweedeInzittende}
            />
          </div>

          {visibleFlight.is_overland && (
            <div style={{ width: 200, marginBottom: 6 }}>
              <p>{$t("Afstand")} (km):</p>
              <Input
                disabled={readOnlyAllData}
                type="number"
                value={visibleFlight.afstand}
                onChange={(elm) =>
                  saveFlight({ afstand: elm.target.value }, $t("Afstand"))
                }
              />
            </div>
          )}

          <div style={{ width: 200, margin: "6px 0" }}>
            <p>{$t("Vliegvelden")}:</p>
            <Input
              addonBefore={<StartIcon />}
              value={visibleFlight.vertrek_vliegveld}
              onChange={(elm) => {
                saveFlight(
                  { vertrek_vliegveld: elm.target.value },
                  $t("Vertrek"),
                );
                // For sleep: if vertrek changed, also update aankomst, sleep always lands on same spot
                sleepFlight &&
                  saveSleepFlight({
                    vertrek_vliegveld: elm.target.value,
                    aankomst_vliegveld: elm.target.value,
                  });
              }}
              disabled={readOnlyAllData}
            />

            <Input
              addonBefore={<LandingIcon />}
              style={{ marginTop: 6 }}
              value={visibleFlight.aankomst_vliegveld}
              onChange={(elm) =>
                saveFlight(
                  { aankomst_vliegveld: elm.target.value },
                  $t("Aankomst"),
                )
              }
              disabled={readOnlyAllData}
            />
          </div>

          {sleepFlight && (
            <div style={{ width: 200, marginBottom: 6 }}>
              <p>{$t("Aankomst sleepkist")}:</p>
              <Input
                value={visibleSleepFlight.aankomst_vliegveld}
                onChange={(elm) =>
                  saveSleepFlight(
                    { aankomst_vliegveld: elm.target.value },
                    $t("Aankomst"),
                  )
                }
                prefix={$t("naar")}
                disabled={readOnlyAllData}
              />
            </div>
          )}

          <Descriptions size="small" column={1} layout="horizontal">
            <Descriptions.Item label={$t("Hoogte")}>
              {flight.height ? <HeightValue value={flight.height} /> : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="IGC">
              <IGCButton flight={flight} title={$t("Open kaart")} />
            </Descriptions.Item>

            <Descriptions.Item>
              {!flight.is_prive && flight.is_locked && (
                <Tag icon={<LockFilled style={{ color: "black" }} />}>
                  {$t("gesloten")}
                </Tag>
              )}
              {!flight.is_prive && !flight.is_locked && (
                <Tag icon={<UnlockOutlined style={{ color: "black" }} />}>
                  {$t("open")}
                </Tag>
              )}

              {flight.is_prive && (
                <Tag icon={<EditFilled style={{ color: "black" }} />}>
                  {$t("zelf toegevoegd")}
                </Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </SettingCard>

        <SettingCard>
          <b className="label">{$t("Notities")}</b>

          <div style={{ maxWidth: 350, marginBottom: 6 }}>
            <p>{$t("Beschrijving (zichtbaar voor iedereen)")}:</p>

            <Input.TextArea
              rows={4}
              disabled={readOnlyAllData && !editInstructorDetails}
              value={visibleFlight.bijzonderheden}
              onChange={(element) => {
                const value = element.target.value.substr(0, 500);
                saveFlight({ bijzonderheden: value }, $t("Aantekeningen"));
                sleepFlight && saveSleepFlight({ bijzonderheden: value });
              }}
            />
          </div>

          <FlightDetails flight={visibleFlight} />

          {sleepFlight && (
            <FlightDetails
              flight={visibleSleepFlight}
              title={$t("Sleepvlucht wijzigingen")}
              hideNotes
            />
          )}
        </SettingCard>

        {flight.gezagvoerder_id && (
          <SignFlightCollapse
            readOnly={false}
            flight={visibleFlight}
            updateFlight={async ({ flight }) => {
              setVisibleFlight(flight);
              onClose();
            }}
          />
        )}
      </Flex>
    </Modal>
  );
};
